<template>
  <div class="ksk-landing">
    <!-- Attract slideshow: tapping anywhere on it starts an order. -->
    <div class="ksk-landing__hero" @click="onHeroClick">
      <swiper
        v-if="isLoadDone && slidesLandingPage.length > 1"
        class="ksk-landing__swiper"
        :slides-per-view="1"
        :space-between="0"
        :loop="true"
        :autoplay="{
          delay: 3000,
          disableOnInteraction: false,
        }"
        :pagination="{
          clickable: true,
          el: '.ksk-landing__dots',
          bulletClass: 'ksk-landing__dot',
          bulletActiveClass: 'ksk-landing__dot--active',
        }"
      >
        <swiper-slide
          v-for="(value, index) in slidesLandingPage"
          :key="index"
        >
          <img class="ksk-landing__slide" :src="value.file_url" alt="" />
        </swiper-slide>
      </swiper>
      <img
        v-else-if="isLoadDone && slidesLandingPage.length === 1"
        class="ksk-landing__slide"
        :src="slidesLandingPage[0].file_url"
        alt=""
      />
      <div class="ksk-landing__shade"></div>

      <!-- KSK054: drives the kiosk i18n locale for the current customer;
           KSK116: every return to Landing resets it to English. -->
      <div class="ksk-landing__lang-picker" @click.stop>
        <button
          type="button"
          class="ksk-landing__lang-toggle"
          aria-haspopup="listbox"
          :aria-expanded="isLanguageMenuOpen"
          @click="isLanguageMenuOpen = !isLanguageMenuOpen"
        >
          <span class="ksk-landing__lang-toggle-icon material-icons-round">language</span>
          <span class="ksk-landing__lang-toggle-code">{{ activeLanguage.toUpperCase() }}</span>
        </button>
        <ul v-if="isLanguageMenuOpen" class="ksk-landing__lang-menu" role="listbox" aria-label="Language">
          <li
            v-for="lang in languages"
            :key="lang.code"
            role="option"
            :aria-selected="activeLanguage === lang.code"
            class="ksk-landing__lang-option"
            :class="{ 'ksk-landing__lang-option--active': activeLanguage === lang.code }"
            @click="selectLanguage(lang.code)"
          >
            <span>{{ lang.label }}</span>
            <span v-if="activeLanguage === lang.code" class="material-icons-round">check</span>
          </li>
        </ul>
      </div>

      <div v-show="slidesLandingPage.length > 1" class="ksk-landing__dots" @click.stop></div>
    </div>

    <div class="ksk-landing__panel" @click="isLanguageMenuOpen = false">
      <h1 class="ksk-landing__title">{{ $t("kiosk.landing.title") }}</h1>

      <!-- Member + Guest -->
      <div v-if="branch && branch.membership_on_kiosk && !branch.enable_retail_mode" class="ksk-landing__ctas">
        <button type="button" class="ksk-landing__cta ksk-landing__cta--primary" v-bind="kskSlot('button_primary')" @click.stop="gotoSignIn">
          <span class="ksk-landing__cta-icon material-icons-round">person</span>
          <span class="ksk-landing__cta-text">
            <span class="ksk-landing__cta-label">{{ $t("kiosk.landing.member") }}</span>
            <span class="ksk-landing__cta-sub">{{ $t("kiosk.landing.memberSub") }}</span>
          </span>
          <span class="ksk-landing__cta-arrow material-icons-round">arrow_forward</span>
        </button>
        <button type="button" class="ksk-landing__cta ksk-landing__cta--outlined" v-bind="kskSlot('button_outlined')" @click.stop="gotoOrder">
          <span class="ksk-landing__cta-icon material-icons-round">storefront</span>
          <span class="ksk-landing__cta-text">
            <span class="ksk-landing__cta-label">{{ $t("kiosk.landing.guest") }}</span>
          </span>
          <span class="ksk-landing__cta-arrow material-icons-round">arrow_forward</span>
        </button>
      </div>

      <!-- Retail: Staff + Guest -->
      <div v-if="branch && branch.enable_retail_mode && branch.staff_on_retail_mode" class="ksk-landing__ctas">
        <button type="button" class="ksk-landing__cta ksk-landing__cta--primary" v-bind="kskSlot('button_primary')" @click.stop="gotoStaffSignIn">
          <span class="ksk-landing__cta-icon material-icons-round">badge</span>
          <span class="ksk-landing__cta-text">
            <span class="ksk-landing__cta-label">{{ $t("kiosk.landing.staff") }}</span>
          </span>
          <span class="ksk-landing__cta-arrow material-icons-round">arrow_forward</span>
        </button>
        <button type="button" class="ksk-landing__cta ksk-landing__cta--outlined" v-bind="kskSlot('button_outlined')" @click.stop="gotoOrder">
          <span class="ksk-landing__cta-icon material-icons-round">storefront</span>
          <span class="ksk-landing__cta-text">
            <span class="ksk-landing__cta-label">{{ $t("kiosk.landing.guest") }}</span>
          </span>
          <span class="ksk-landing__cta-arrow material-icons-round">arrow_forward</span>
        </button>
      </div>

      <!-- KSK110: Retail without staff sign-in: Guest only -->
      <div v-if="branch && branch.enable_retail_mode && !branch.staff_on_retail_mode" class="ksk-landing__ctas ksk-landing__ctas--single">
        <button type="button" class="ksk-landing__cta ksk-landing__cta--primary ksk-landing__cta--center" v-bind="kskSlot('button_primary')" @click.stop="gotoOrder">
          <span class="ksk-landing__cta-icon material-icons-round">storefront</span>
          <span class="ksk-landing__cta-label">{{ $t("kiosk.landing.guest") }}</span>
          <span class="ksk-landing__cta-arrow material-icons-round">arrow_forward</span>
        </button>
      </div>

      <!-- Start Order only -->
      <div v-if="branch && !branch.enable_retail_mode && !branch.membership_on_kiosk" class="ksk-landing__ctas ksk-landing__ctas--single">
        <button type="button" class="ksk-landing__cta ksk-landing__cta--primary ksk-landing__cta--center" v-bind="kskSlot('button_primary')" @click.stop="gotoOrder">
          <span class="ksk-landing__cta-icon material-icons-round">restaurant_menu</span>
          <span class="ksk-landing__cta-label">{{ $t("kiosk.landing.startOrder") }}</span>
          <span class="ksk-landing__cta-arrow material-icons-round">arrow_forward</span>
        </button>
      </div>
    </div>
    <ModalSignInNew @continue-guest="gotoOrder"></ModalSignInNew>
    <ModalStaffSignIn :keyReload="keyReloadStaffSignIn"></ModalStaffSignIn>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Pagination, Autoplay } from "swiper";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";
import MaterialLoading from "@/components/MaterialLoading.vue";
import { localStorageName } from "@/config/utils";
import ModalSignInNew from "../../views/kiosk/components/ModalSignInNew.vue";
import OrderService from "../../services/order.service";
import { collectItemNames, loadMenuTranslations, startKioskMenuWarm } from "@/i18n/menuTranslate";
import $ from "jquery";
import { kskSlot } from "@/config/kioskColorSlots";
import memberService from "../../services/member.service";
import ModalStaffSignIn from "../../views/kiosk/components/ModalStaffSignIn.vue";
import { setKioskLocale, DEFAULT_LOCALE } from "@/i18n";

SwiperCore.use([Pagination, Autoplay]);

export default {
  name: "LandingPageKiosk",
  components: {
    Swiper,
    SwiperSlide,
    MaterialLoading,
    ModalSignInNew,
    ModalStaffSignIn,
  },
  data() {
    return {
      isLoadDone: false,
      loaderActive: false,
      message: "",
      snackbar: null,
      isWaitingTime: true,
      prefixUrl: "",
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO"), "")
      ),
      merchantCode: "",
      branchCode: "",
      slidesLandingPage: [],
      memberInfo: JSON.parse(
        localStorage.getItem(localStorageName("memberInfo"), "")
      ),
      keyReloadStaffSignIn: 'keyReloadStaffSignIn',
      staffInfo: JSON.parse(
        localStorage.getItem(localStorageName("STAFF_INFO"), "")
      ),
      languages: [
        { code: "en", label: "English" },
        { code: "zh", label: "中文" },
        { code: "ja", label: "日本語" },
        { code: "ko", label: "한국어" },
        { code: "th", label: "ไทย" },
      ],
      isLanguageMenuOpen: false,
    };
  },
  computed: {
    activeLanguage() {
      return this.$i18n.locale;
    },
  },
  mounted() {
    $(".modal-backdrop.fade.show").remove();
  },
  beforeMount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
  },
  beforeUnmount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
  },
  async created() {
    this.merchantCode = this.$store.state.merchantCode;
    this.branchCode = this.$store.state.branchCode;
    this.getSlides();

    // KSK095: timeout, Start Over and New Order all land here, so every visit
    // starts a clean customer session.
    this.resetKioskSession();

    // KSK123: translate the current menuset into every language now, so the
    // language buttons switch at once; re-checked every 15 minutes.
    startKioskMenuWarm(this.loadMenuNames);
  },
  methods: {
    kskSlot,
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),

    gotoOrder() {
      if (this.branch && this.branch.enable_retail_mode) {
        this.$router.push({
          name: "CheckoutPageKiosk",
          params: {
            merchant_code: this.merchantCode,
            branch_code: this.branchCode,
          },
        });
      } else {
        this.$router.push({
          name: "OrderPageKiosk",
          params: {
            merchant_code: this.merchantCode,
            branch_code: this.branchCode,
          },
        });
      }
    },

    // While the language menu is open, a tap outside only closes it.
    onHeroClick() {
      if (this.isLanguageMenuOpen) {
        this.isLanguageMenuOpen = false;
        return;
      }
      this.gotoOrder();
    },

    selectLanguage(code) {
      setKioskLocale(code);
      this.isLanguageMenuOpen = false;
    },

    // KSK095: drops the previous customer's member/staff login, cart and
    // order choices. Device keys (BRANCH_INFO, MERCHANT_*, KIOSK_MODE,
    // colours) stay, or the kiosk would lose its own setup.
    // KSK116: the language is the customer's choice, so it goes back to English.
    resetKioskSession() {
      if (
        localStorage.getItem(localStorageName("token")) ||
        localStorage.getItem(localStorageName("KIOSK_MEMBER_INFO"))
      ) {
        // Clears member, KIOSK_MEMBER_INFO and token locally even if the API fails.
        memberService.logout(this.merchantCode).catch(() => {});
      }
      [
        "member",
        "memberInfo",
        "KIOSK_MEMBER_INFO",
        "token",
        "STAFF_INFO",
        "PASSCODE_CONFIRMED",
        "ORDER_INFO",
        "DINING_OPTION",
        "PAYMENT_METHOD",
        "SELECTED_REWARD",
        "SELECTED_REWARD_FROM_ORDER_HISTORY",
        "KIOSK_REWARD_JUST_APPLIED",
        "KIOSK_PROMO_CODE_APPLIED",
      ].forEach((name) => localStorage.removeItem(localStorageName(name)));
      setKioskLocale(DEFAULT_LOCALE);
      // Item names too: retail goes straight to Checkout, which keeps the last
      // customer's translated names while the locale is already English.
      loadMenuTranslations([], DEFAULT_LOCALE);
      this.memberInfo = null;
      this.staffInfo = null;
    },

    // Every name the kiosk menu shows (categories and their items), read
    // quietly with no loader. Kept free of `this` so the 15-minute timer can
    // call it after Landing has gone.
    loadMenuNames() {
      const branch = JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO")) || "null"
      );
      if (!branch) return [];
      return OrderService.getListCategory({
        menuset_id: branch.menuset_id ?? 0,
        branch_code: branch.branch_code ?? "",
      }).then((response) =>
        response.data.result.isSuccess
          ? collectItemNames(response.data.data.categories || [])
          : []
      );
    },

    gotoStaffSignIn() {
      this.keyReloadStaffSignIn = Math.random().toString();
      $(".btn-show-modal-staff-signin").click();
    },

    gotoSignIn() {
      $(".btn-show-modal-signin").click();
    },

    getSlides() {
      this.isLoadDone = false;
      this.showLoader();
      var dataForm = {
        isMintyApi: true,
        branch_code: this.branchCode,
        where_showing: 1,
        is_kiosk: 1,
      };
      OrderService.getSlidesLandingPage(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.slidesLandingPage = response.data.data;
            this.isLoadDone = true;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.isLoadDone = true;
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.hideLoader();
        }
      );
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>

<style scoped>
/* KSK006 — S1 Landing: attract slideshow (~75%) over a white bottom panel. */
.ksk-landing {
  --ksk-landing-panel-height: 304px; /* KSK050: was 488px (KSK040), 480px, 560px */
  --ksk-landing-lang-width: 168px;
  position: relative;
  height: calc(var(--ksk-vh, 1vh) * 100);
  overflow: hidden;
  background: var(--ksk-background);
  font-family: var(--ksk-font);
  user-select: none;
}

/* Hero — KSK067: runs to the foot of the screen, behind the 70% panel. */
.ksk-landing__hero {
  position: absolute;
  inset: 0;
  overflow: hidden;
  cursor: pointer;
  background: linear-gradient(160deg, var(--ksk-primary), var(--ksk-primary-pressed));
}
.ksk-landing__swiper,
.ksk-landing__swiper :deep(.swiper-wrapper),
.ksk-landing__swiper :deep(.swiper-slide) {
  width: 100%;
  height: 100%;
}
.ksk-landing__slide {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ksk-landing__shade {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0) 22%,
    rgba(0, 0, 0, 0) 68%,
    rgba(0, 0, 0, 0.45) 100%
  );
}

/* KSK050 — language picker: white plate top-right, opening a dropdown list below it. */
.ksk-landing__lang-picker {
  position: absolute;
  top: var(--ksk-space-4);
  right: var(--ksk-space-4);
  z-index: 4;
}
.ksk-landing__lang-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ksk-space-1);
  width: var(--ksk-landing-lang-width);
  height: 80px;
  padding: 0 var(--ksk-space-2);
  border: none;
  border-radius: var(--ksk-radius-pill); /* KSK060: was 22px */
  background: var(--ksk-surface);
  box-shadow: 0 6px 24px rgba(20, 26, 20, 0.18);
  font-family: var(--ksk-font);
  color: var(--ksk-text);
  cursor: pointer;
  transition: transform 80ms ease-out;
  -webkit-tap-highlight-color: transparent;
}
.ksk-landing__lang-toggle:active {
  transform: scale(0.97);
}
.ksk-landing__lang-toggle-icon {
  font-size: 40px;
  color: var(--ksk-primary);
}
.ksk-landing__lang-toggle-code {
  font-size: 30px;
  line-height: 38px;
  font-weight: 700;
}
.ksk-landing__lang-menu {
  position: absolute;
  top: calc(100% + var(--ksk-space-1));
  right: 0;
  min-width: 280px;
  margin: 0;
  padding: 8px;
  list-style: none;
  border-radius: 22px;
  background: var(--ksk-surface);
  box-shadow: 0 6px 24px rgba(20, 26, 20, 0.18);
}
.ksk-landing__lang-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ksk-space-2);
  height: 72px;
  padding: 0 var(--ksk-space-3);
  border-radius: 14px;
  font-size: 26px;
  line-height: 32px;
  font-weight: 600;
  color: var(--ksk-text);
  white-space: nowrap;
  cursor: pointer;
}
.ksk-landing__lang-option:active {
  background: var(--ksk-surface-muted);
}
.ksk-landing__lang-option--active {
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
}
.ksk-landing__lang-option .material-icons-round {
  font-size: 32px;
}

/* Pill pagination dots (bullets are created by Swiper, hence :deep). */
.ksk-landing__dots {
  position: absolute;
  left: var(--ksk-space-4);
  bottom: calc(var(--ksk-landing-panel-height) + var(--ksk-powered-height) + 44px);
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
}
.ksk-landing__dots :deep(.ksk-landing__dot) {
  display: block;
  width: 14px;
  height: 14px;
  border-radius: var(--ksk-radius-pill);
  background: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  transition: width 240ms ease-out, background-color 240ms ease-out;
}
.ksk-landing__dots :deep(.ksk-landing__dot--active) {
  width: 48px;
  background: #ffffff;
}

/* White bottom panel. */
.ksk-landing__panel {
  position: absolute;
  left: 0;
  right: 0;
  bottom: var(--ksk-powered-height);
  z-index: 3;
  display: flex;
  flex-direction: column;
  height: var(--ksk-landing-panel-height);
  padding: 48px var(--ksk-page-padding) var(--ksk-space-4);
  border-radius: var(--ksk-radius-popup) var(--ksk-radius-popup) 0 0;
  /* KSK069: only the white bar is 75% transparent (was 95%, then 85%); the CTAs stay solid.
     Was opacity 0.7 on the whole panel (KSK067). */
  background: rgba(255, 255, 255, 0.25);
}
@supports (color: color-mix(in srgb, red 50%, blue)) {
  .ksk-landing__panel {
    background: color-mix(in srgb, var(--ksk-surface) 25%, transparent);
  }
}
.ksk-landing__title {
  margin: 0;
  font-family: var(--ksk-font);
  font-size: var(--ksk-headline-size);
  line-height: var(--ksk-headline-lh);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ksk-text);
}

.ksk-landing__ctas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--ksk-gutter);
  margin: auto 0 0; /* KSK050: sits at the panel bottom */
}
/* KSK060: Start Order alone keeps the Member/Guest button width, centred. */
.ksk-landing__ctas--single {
  justify-content: center;
  grid-template-columns: calc((100% - var(--ksk-gutter)) / 2);
}
.ksk-landing__cta {
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  height: 128px; /* KSK040: was 96px */
  padding: 0 36px 0 26px; /* KSK060: inset from the pill ends */
  border: 2px solid transparent;
  border-radius: var(--ksk-radius-pill); /* KSK060: was --ksk-radius-button */
  font-family: var(--ksk-font);
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition: transform 80ms ease-out;
  -webkit-tap-highlight-color: transparent;
}
.ksk-landing__cta::after {
  content: "";
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
  pointer-events: none;
}
.ksk-landing__cta:active {
  transform: scale(0.98);
}
.ksk-landing__cta:active::after {
  opacity: 0.08;
}
/* KSK063: the landing CTAs follow the button_primary / button_outlined
   colour slots; theme values otherwise. */
.ksk-landing__cta--primary {
  background: var(--ksk-slot-accent, var(--ksk-primary));
  border-color: var(--ksk-slot-outline, transparent);
  color: var(--ksk-slot-font, var(--ksk-on-primary));
  box-shadow: var(--ksk-shadow-float);
}
.ksk-landing__cta--outlined {
  background: var(--ksk-slot-accent, var(--ksk-surface));
  border-color: var(--ksk-slot-outline, var(--ksk-primary));
  color: var(--ksk-slot-font, var(--ksk-primary));
}
.ksk-landing__cta--center {
  justify-content: center;
}
.ksk-landing__cta-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 40px;
  background: rgba(255, 255, 255, 0.16);
}
.ksk-landing__cta--outlined .ksk-landing__cta-icon {
  background: var(--ksk-primary-container);
}
.ksk-landing__cta-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.ksk-landing__cta--center .ksk-landing__cta-text {
  flex: none;
}
.ksk-landing__cta-label {
  font-size: 36px;
  line-height: 44px;
  font-weight: 700;
}
.ksk-landing__cta-sub {
  font-size: 22px;
  line-height: 28px;
  font-weight: 500;
  opacity: 0.8;
}
.ksk-landing__cta-arrow {
  font-size: 40px;
}
.ksk-landing__cta:not(.ksk-landing__cta--center) .ksk-landing__cta-arrow {
  margin-left: auto;
}

</style>
